import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

const normalizeValue = (value) => {
    if (value === null || value === undefined) return '';
    const normalized = String(value).trim().toLowerCase();
    return normalized.endsWith('s') ? normalized.slice(0, -1) : normalized;
};

const formatSlotDuration = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
        return `${numeric}s`;
    }
    return String(value);
};

const SlotDurationItem = ({ slotDuration, isChecked, onToggle }) => {
    const handleChange = (event) => {
        onToggle(event.target.checked);
    };

    const testIdSuffix = String(slotDuration.id);

    return (
        <div
            className="flex items-center justify-between my-2"
            data-testid={`slot-duration-item-${testIdSuffix}`}>
            <div className="flex items-center">
                <input
                    checked={isChecked}
                    onChange={handleChange}
                    name={`slot-duration-${slotDuration.id}`}
                    id={`slot-duration-${slotDuration.id}`}
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-0 border-gray-300 rounded"
                    data-testid={`slot-duration-checkbox-${testIdSuffix}`}
                />
                <label
                    htmlFor={`slot-duration-${slotDuration.id}`}
                    className="ml-2 flex items-center text-sm text-gray-600"
                    data-testid={`slot-duration-label-${testIdSuffix}`}>
                    <span>{slotDuration.label}</span>
                    <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-400">
                        {slotDuration.count ?? 0}
                    </span>
                </label>
            </div>
        </div>
    );
};

SlotDurationItem.propTypes = {
    slotDuration: PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
    }).isRequired,
    isChecked: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
};

const SlotDurationList = ({
    screens = [],
    allScreens = [],
    selectedSlotDurations = [],
    availableSlotDurations = [],
    availableSlotDurationCounts = [],
    onChange,
    isFilterByAudiences = false,
}) => {
    const { t } = useTranslation();

    const baseScreens = useMemo(() => {
        const dataset = Array.isArray(allScreens) && allScreens.length
            ? allScreens
            : screens;
        if (!Array.isArray(dataset)) return [];
        if (!isFilterByAudiences) return dataset;
        return dataset.filter((screen) => screen?.hasAudience || screen?.audiences);
    }, [allScreens, screens, isFilterByAudiences]);

    const selectedNormalized = useMemo(
        () =>
            new Set(
                (selectedSlotDurations || [])
                    .map((value) => normalizeValue(value))
                    .filter(Boolean)
            ),
        [selectedSlotDurations]
    );

    const slotDurations = useMemo(() => {
        const map = new Map();
        const slotDurationCountLookup = new Map();
        (availableSlotDurationCounts || []).forEach((item) => {
            const key = normalizeValue(item?.value);
            const count = Number(item?.count ?? 0);
            if (!key || !Number.isFinite(count)) return;
            slotDurationCountLookup.set(key, count);
        });
        const normalizedAvailableSlotDurations = Array.isArray(
            availableSlotDurations
        )
            ? availableSlotDurations
                  .map((value) => normalizeValue(value))
                  .filter(Boolean)
            : [];

        if (normalizedAvailableSlotDurations.length > 0) {
            normalizedAvailableSlotDurations.forEach((value) => {
                if (!value || map.has(value)) return;
                map.set(value, {
                    id: value,
                    label: formatSlotDuration(value),
                    count: slotDurationCountLookup.get(value) ?? 0,
                });
            });
        } else {
            baseScreens.forEach((screen) => {
                const raw = screen?.slot_duration ?? screen?.slotDuration;
                if (raw === null || raw === undefined || raw === '') return;

                const id = normalizeValue(raw);
                if (!id) return;

                const label = formatSlotDuration(raw);
                if (map.has(id)) {
                    map.get(id).count += 1;
                } else {
                    map.set(id, {
                        id,
                        label,
                        count: slotDurationCountLookup.get(id) ?? 1,
                    });
                }
            });
        }

        selectedNormalized.forEach((value) => {
            if (!value || map.has(value)) return;
            const original = (selectedSlotDurations || []).find(
                (item) => normalizeValue(item) === value
            );
            const display = formatSlotDuration(original ?? value);
            map.set(value, {
                id: value,
                label: display,
                count: slotDurationCountLookup.get(value) ?? 0,
            });
        });

        return Array.from(map.values()).sort((a, b) => {
            const aHas = (a.count ?? 0) > 0 ? 0 : 1;
            const bHas = (b.count ?? 0) > 0 ? 0 : 1;
            if (aHas !== bHas) return aHas - bHas;
            return a.label.localeCompare(b.label);
        });
    }, [
        baseScreens,
        selectedSlotDurations,
        selectedNormalized,
        availableSlotDurations,
        availableSlotDurationCounts,
    ]);

    const isSelected = (slotDuration) => selectedNormalized.has(slotDuration.id);

    const updateSelection = (slotDuration, checked) => {
        if (!onChange) return;
        if (checked) {
            if (isSelected(slotDuration)) return;
            onChange([...selectedSlotDurations, slotDuration.id]);
        } else {
            onChange(
                selectedSlotDurations.filter(
                    (item) => normalizeValue(item) !== slotDuration.id
                )
            );
        }
    };

    if (slotDurations.length === 0) return null;

    return (
        <div
            className="font-light pt-2 w-full"
            data-testid="slot-duration-list">
            <div className="title font-light py-2 shadow-sm">
                {t(
                    'sections.campaigns.new-campaign.wizard-screens.filters.slotDurations-label'
                )}
            </div>
            <div className="max-h-60 overflow-y-auto overflow-hidden w-full">
                {slotDurations.map((slotDuration) => (
                    <SlotDurationItem
                        key={slotDuration.id}
                        slotDuration={slotDuration}
                        isChecked={isSelected(slotDuration)}
                        onToggle={(checked) =>
                            updateSelection(slotDuration, checked)
                        }
                    />
                ))}
            </div>
        </div>
    );
};

SlotDurationList.propTypes = {
    screens: PropTypes.array,
    allScreens: PropTypes.array,
    selectedSlotDurations: PropTypes.array,
    availableSlotDurations: PropTypes.array,
    availableSlotDurationCounts: PropTypes.array,
    onChange: PropTypes.func,
    isFilterByAudiences: PropTypes.bool,
};

export default SlotDurationList;
